import { useState, useEffect } from "react";
import apiRequest, { API_URLS } from "../services/api";
import Alert from "../components/Alert";

const Icon = ({ d, className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={d} />
  </svg>
);

const ICONS = {
  globe:   "M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z",
  plus:    "M12 4v16m8-8H4",
  users:   "M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z",
  trash:   "M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16",
  eye:     "M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z",
  warning: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z",
  x:       "M6 18L18 6M6 6l12 12",
  search:  "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z",
};

const PALETTE_COLORS = [
  "bg-blue-600", "bg-emerald-600", "bg-violet-600",
  "bg-indigo-600", "bg-amber-500", "bg-teal-600",
  "bg-rose-600", "bg-cyan-600", "bg-orange-500", "bg-purple-600",
];

export default function Regions() {
  const [regions, setRegions]                 = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [regionToDelete, setRegionToDelete]   = useState(null);
  const [showCreateForm, setShowCreateForm]   = useState(false);
  const [formData, setFormData]               = useState({ name: "" });
  const [submitting, setSubmitting]           = useState(false);
  const [search, setSearch]                   = useState("");

  useEffect(() => { fetchRegions(); }, []);

  const fetchRegions = async () => {
    try {
      const r = await apiRequest(API_URLS.admin.getRegions);
      setRegions(r.data || []);
    } catch (err) {
      setError(err.message || "Failed to fetch regions");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { setError("Region name is required"); return; }
    setSubmitting(true);
    try {
      await apiRequest(API_URLS.admin.createRegion, "POST", { name: formData.name });
      setFormData({ name: "" });
      setShowCreateForm(false);
      fetchRegions();
    } catch (err) {
      setError(err.message || "Failed to create region");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteRegion = async () => {
    if (!regionToDelete) return;
    try {
      await apiRequest(API_URLS.admin.deleteRegion(regionToDelete), "DELETE");
      setShowDeleteConfirm(false);
      setRegionToDelete(null);
      fetchRegions();
    } catch (err) {
      setError(err.message || "Failed to delete region");
    }
  };

  const filtered = regions.filter(r =>
    r.name?.toLowerCase().includes(search.toLowerCase())
  );

  const regionToDeleteName = regions.find(r => r.id === regionToDelete)?.name;

  return (
    <div className="min-h-screen bg-slate-50">

      {/* ── Page header ─────────────────────────────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 px-6 py-5 sticky top-0 z-10 shadow-sm">
        <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-slate-900 tracking-tight">Regions</h1>
            <p className="text-xs text-slate-400 mt-0.5">Manage survey regions and enumerator assignments</p>
          </div>
          <button
            onClick={() => setShowCreateForm(true)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors"
          >
            <Icon d={ICONS.plus} className="w-4 h-4" />
            Create Region
          </button>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {error && <Alert type="error" message={error} onClose={() => setError("")} />}

        {/* ── Summary strip ──────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {[
            { label: "Total Regions",   value: regions.length,                                          color: "bg-blue-600" },
            { label: "Total Users",     value: regions.reduce((s, r) => s + (r.users?.length || 0), 0), color: "bg-emerald-600" },
            { label: "Avg Users / Region", value: regions.length > 0 ? (regions.reduce((s,r) => s+(r.users?.length||0), 0) / regions.length).toFixed(1) : "0", color: "bg-violet-600" },
          ].map((c, i) => (
            <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 flex items-center gap-3">
              <div className={`w-9 h-9 ${c.color} rounded-lg flex items-center justify-center shrink-0`}>
                <Icon d={ICONS.globe} className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-900 leading-none">{c.value}</p>
                <p className="text-xs text-slate-500 mt-1 font-medium">{c.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Regions list ───────────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">

          {/* Toolbar */}
          <div className="px-5 py-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-slate-800">All Regions</h2>
              <p className="text-xs text-slate-400 mt-0.5">{filtered.length} of {regions.length} regions</p>
            </div>
            {/* Search */}
            <div className="relative max-w-xs w-full sm:w-auto">
              <Icon d={ICONS.search} className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search regions…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <Icon d={ICONS.x} className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Body */}
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="relative w-10 h-10">
                <div className="absolute inset-0 rounded-full border-4 border-blue-100" />
                <div className="absolute inset-0 rounded-full border-4 border-blue-600 border-t-transparent animate-spin" />
              </div>
              <p className="text-sm text-slate-400">Loading regions…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-slate-400">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                <Icon d={ICONS.globe} className="w-6 h-6 text-slate-300" />
              </div>
              <p className="text-sm font-medium">
                {search ? `No regions match "${search}"` : "No regions yet"}
              </p>
              {!search && (
                <button
                  onClick={() => setShowCreateForm(true)}
                  className="text-xs text-blue-600 hover:text-blue-800 font-semibold"
                >
                  Create your first region →
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((region, i) => {
                const userCount  = region.users?.length || 0;
                const dotColor   = PALETTE_COLORS[i % PALETTE_COLORS.length];
                return (
                  <div
                    key={region.id}
                    className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50/70 transition-colors group"
                  >
                    {/* Colour dot / avatar */}
                    <div className={`w-9 h-9 ${dotColor} rounded-lg flex items-center justify-center shrink-0 text-white text-xs font-bold`}>
                      {region.name?.charAt(0).toUpperCase()}
                    </div>

                    {/* Name + meta */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-800 truncate">{region.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <Icon d={ICONS.users} className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-xs text-slate-500">
                          {userCount} {userCount === 1 ? "user" : "users"} assigned
                        </span>
                      </div>
                    </div>

                    {/* User count badge */}
                    <span className={`hidden sm:inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold border
                      ${userCount > 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`}>
                      {userCount} {userCount === 1 ? "user" : "users"}
                    </span>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      <button className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 hover:bg-blue-100 transition-colors">
                        <Icon d={ICONS.eye} className="w-3.5 h-3.5" />
                        View Users
                      </button>
                      <button
                        onClick={() => { setRegionToDelete(region.id); setShowDeleteConfirm(true); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-red-50 text-red-600 border border-red-100 hover:bg-red-100 transition-colors"
                      >
                        <Icon d={ICONS.trash} className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Create Region Modal ──────────────────────────────────────────────── */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            {/* Modal header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Icon d={ICONS.globe} className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-bold text-slate-900">Create New Region</h2>
                  <p className="text-xs text-slate-400">Add a new geographic region</p>
                </div>
              </div>
              <button
                onClick={() => { setShowCreateForm(false); setFormData({ name: "" }); }}
                className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              >
                <Icon d={ICONS.x} className="w-4 h-4" />
              </button>
            </div>

            {/* Modal body */}
            <div className="px-6 py-5">
              <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                Region Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({ name: e.target.value })}
                onKeyDown={e => e.key === "Enter" && handleSubmit(e)}
                className="w-full px-3.5 py-2.5 text-sm border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition bg-slate-50 focus:bg-white"
                placeholder="e.g. Nairobi North, Coast Region…"
                autoFocus
              />
              <p className="text-xs text-slate-400 mt-2">
                Give the region a clear, descriptive name that field agents will recognise.
              </p>
            </div>

            {/* Modal footer */}
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={handleSubmit}
                disabled={submitting || !formData.name.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-xl transition-colors"
              >
                {submitting ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Icon d={ICONS.plus} className="w-4 h-4" />
                )}
                {submitting ? "Creating…" : "Create Region"}
              </button>
              <button
                onClick={() => { setShowCreateForm(false); setFormData({ name: "" }); }}
                className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Confirmation Modal ────────────────────────────────────────── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            {/* Icon + title */}
            <div className="flex flex-col items-center text-center px-6 pt-8 pb-5">
              <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mb-4 ring-8 ring-red-50">
                <Icon d={ICONS.warning} className="w-7 h-7 text-red-500" />
              </div>
              <h2 className="text-lg font-bold text-slate-900">Delete Region</h2>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                Are you sure you want to delete{" "}
                <span className="font-semibold text-slate-800">"{regionToDeleteName}"</span>?
                This will remove all user assignments and cannot be undone.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3 px-6 pb-6">
              <button
                onClick={confirmDeleteRegion}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                <Icon d={ICONS.trash} className="w-4 h-4" />
                Delete Region
              </button>
              <button
                onClick={() => { setShowDeleteConfirm(false); setRegionToDelete(null); }}
                className="flex-1 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}